/**
 * Allowed merge fields for campaign templates (fase 3), per
 * `../bilag/arkitektur-2026-09-23.md` §7.5: "Liquid-subset
 * (`{{ contact.first_name | default: "there" }}`, `{{ coupon_code }}`,
 * `{{ unsubscribe_url }}`, `{{ view_url }}`) kompileret til SES
 * Handlebars; alt udenfor afvises."
 *
 * `company_address` is this task's own addition, not named in that list:
 * the footer requirement (firmaadresse + afmeldingslink, same doc §7.3)
 * needs *some* way to reference the tenant's postal address without
 * hard-coding it into every template, and the real 1/9 newsletter's
 * Mailchimp tag `*|LIST:ADDRESS|*` / `*|HTML:LIST_ADDRESS_HTML|*` (see
 * `docs/fase0/eksempel-nyhedsbrev-2026-09-01.md`) maps most naturally to
 * exactly this kind of field. It is resolved server-side from tenant
 * data, the same way `unsubscribe_url` is — never contact PII. Flagged
 * here for the next agent to confirm or rename. Since Terje's decision
 * 6/10-2026 it holds the brand kit's "Sender details" (who is sending,
 * e.g. "NoPain® Cream") — a postal address is no longer required; the name
 * stays so stored templates keep working.
 *
 * `legal_notice_url` (6/10-2026): the brand kit's optional legal notice /
 * Impressum link (`brand_kit.legal_notice_url`, https only), resolved
 * server-side like `company_address` (send/consumer.ts, the view page in
 * apps/edge). Meant for the footer's "Legal notice" link.
 *
 * `flow_coupon_code` (fase 5, docs/fase5/design.md WP2 brief) is this
 * package's own addition on top of that list: a flow's `create_coupon`
 * step writes the code it created into `contact_fields.data` under
 * `_flow_<slug>_coupon` (`db/tenant/contacts.repo.ts#setContactField`),
 * and a later `send_template` step in the same flow needs a merge field
 * to reference it in its subject/body. Resolving it into
 * `replacementData` per recipient is `flows/` territory (see
 * `flows/couponMergeField.ts`) — this module only allow-lists the field
 * name itself, same as `coupon_code`.
 *
 * `contact.last_name` (25/9) reads `contact_fields.data.last_name`, the
 * same way `contact.first_name` reads `first_name`. Send-time resolution
 * of all of these lives in send/personalize.ts.
 *
 * `preferences_url` (28/9, preference centre): a signed, never-expiring
 * link to the recipient's own preference centre (`apps/edge/src/routes/
 * preferenceCentre.ts`), same token scheme as `unsubscribe_url`/
 * `view_url`. Resolved server-side per recipient in
 * `send/recipientLinks.ts#buildRecipientMailData` — never contact PII by
 * itself, just an unguessable capability link, exactly like the other two
 * link fields. Meant for the footer, alongside `unsubscribe_url`.
 *
 * `review_star_1_url`..`review_star_5_url` (28/9, anmeldelsesflow): the
 * five star links a review-request send shows (`flows/builtins/
 * review.ts`). One `purpose: 'review'` token per message, five URLs
 * differing only by `?s=N` — resolved in `send/consumer.ts#buildJobs`
 * (not `buildRecipientMailData`, unlike the other link fields, since it
 * is one-token-to-many-URLs rather than one-token-to-one-URL).
 *
 * `cart_items` (8/10, abandoned-cart flow mails): the products the shopper
 * left in the cart — an email-safe HTML block (heading plus "product ×
 * quantity" rows) in the HTML part, a plain list in the text part. Only
 * the abandoned-cart flow's mails (a flow step whose run was enrolled by a
 * cart) can fill it: it is resolved per recipient in
 * `send/consumer.ts#buildJobs` from that cart's `cart_events` row (names
 * and quantities only, no prices). It renders as nothing when the cart is
 * gone, empty or already recovered, so place it alone in a padding-free
 * table cell and never let the surrounding copy depend on it. Elsewhere
 * (campaigns, view-in-browser, previews) it renders as nothing or sample
 * products. Details: `send/cartItems.ts`.
 */

/** Field paths a template's `{{ ... }}` placeholders may reference. Dotted paths use Handlebars' native nested-property access. */
export const MERGE_FIELDS = [
  'contact.first_name',
  'contact.last_name',
  'coupon_code',
  'unsubscribe_url',
  'view_url',
  'company_address',
  'legal_notice_url',
  'flow_coupon_code',
  'preferences_url',
  'review_star_1_url',
  'review_star_2_url',
  'review_star_3_url',
  'review_star_4_url',
  'review_star_5_url',
  'cart_items',
] as const;

export type MergeField = (typeof MERGE_FIELDS)[number];

const MERGE_FIELD_SET: ReadonlySet<string> = new Set(MERGE_FIELDS);

export function isAllowedMergeField(field: string): field is MergeField {
  return MERGE_FIELD_SET.has(field);
}

/**
 * Matches one `{{ field }}` or `{{ field | default: "text" }}` placeholder.
 * Field paths: `[a-zA-Z0-9_]+(\.[a-zA-Z0-9_]+)*`. The default filter's
 * value is a double-quoted string with `\"` and `\\` as its only escapes
 * (mirrors what `escapeDefaultLiteral` in `liquid.ts` produces).
 */
export const MERGE_FIELD_PATTERN =
  /\{\{\s*([a-zA-Z0-9_]+(?:\.[a-zA-Z0-9_]+)*)\s*(?:\|\s*default:\s*"((?:\\.|[^"\\])*)")?\s*\}\}/g;

export interface FoundMergeField {
  field: string;
  defaultValue: string | undefined;
  raw: string;
  index: number;
}

/** Finds every `{{ ... }}` placeholder in `html`, known or not — callers decide what to do with unknown ones. */
export function findMergeFields(html: string): FoundMergeField[] {
  const found: FoundMergeField[] = [];
  for (const match of html.matchAll(MERGE_FIELD_PATTERN)) {
    found.push({
      field: match[1]!,
      defaultValue: match[2] === undefined ? undefined : match[2].replace(/\\(.)/g, '$1'),
      raw: match[0],
      index: match.index,
    });
  }
  return found;
}

/** Every placeholder field path in `html` that is not in {@link MERGE_FIELDS}, de-duplicated, in first-seen order. */
export function findUnknownMergeFields(html: string): string[] {
  const seen = new Set<string>();
  const unknown: string[] = [];
  for (const { field } of findMergeFields(html)) {
    if (!isAllowedMergeField(field) && !seen.has(field)) {
      seen.add(field);
      unknown.push(field);
    }
  }
  return unknown;
}
